import * as THREE from 'three';
import { PieceType, Square, MoveDetail } from '../core/types.ts';
import { PieceObject, PieceViewManager, squareToCoords } from './pieceView.ts';
import { SoundManager } from '../audio/soundManager.ts';
import { CameraController } from './cameraController.ts';

interface ActiveMoveAnim {
  piece: PieceObject;
  startPos: THREE.Vector3;
  endPos: THREE.Vector3;
  type: PieceType;
  duration: number;
  elapsed: number;
  onComplete: () => void;
  soundPlayed: boolean;
}

interface ShatterShard {
  mesh: THREE.Mesh;
  velocity: THREE.Vector3;
  rotationSpeed: THREE.Vector3;
  life: number;
  maxLife: number;
}

export class AnimationManager {
  private pieceView: PieceViewManager;
  private soundManager: SoundManager;
  private cameraController: CameraController;
  private scene: THREE.Scene;

  private activeMoves: ActiveMoveAnim[] = [];
  private activeShards: ShatterShard[] = [];
  private particleGroup: THREE.Group;

  public animationSpeed: 'normal' | 'fast' | 'instant' = 'normal';
  public cinematicKills = true;

  constructor(
    scene: THREE.Scene,
    pieceView: PieceViewManager,
    soundManager: SoundManager,
    cameraController: CameraController
  ) {
    this.scene = scene;
    this.pieceView = pieceView;
    this.soundManager = soundManager;
    this.cameraController = cameraController;

    this.particleGroup = new THREE.Group();
    this.particleGroup.name = 'DestructionParticles';
    this.scene.add(this.particleGroup);
  }

  public get isBusy(): boolean {
    return this.activeMoves.length > 0;
  }

  public animateMove(
    move: MoveDetail,
    onFinish: () => void
  ): void {
    if (this.animationSpeed === 'instant') {
      this.executeInstant(move, onFinish);
      return;
    }

    const movingPiece = this.pieceView.getPieceAt(move.from);
    if (!movingPiece) {
      onFinish();
      return;
    }

    const speedMultiplier = this.animationSpeed === 'fast' ? 0.5 : 1.0;
    const destCoords = squareToCoords(move.to);
    const endPos = new THREE.Vector3(destCoords.x, movingPiece.originalY, destCoords.z);
    const startPos = movingPiece.mesh.position.clone();

    // Base duration depends on piece type
    let baseDuration = 0.55;
    if (movingPiece.type === 'n') baseDuration = 0.65;
    if (movingPiece.type === 'r') baseDuration = 0.6;
    if (movingPiece.type === 'q') baseDuration = 0.5;

    const duration = baseDuration * speedMultiplier;

    // Optional camera focus during capture
    if (move.captured && this.cinematicKills) {
      this.cameraController.focusOnTarget(endPos, (duration + 0.6) * 1000);
    }

    // Play initial movement sound
    if (movingPiece.type === 'n') {
      this.soundManager.playKnightHop();
    } else if (movingPiece.type === 'q' || movingPiece.type === 'b') {
      this.soundManager.playSpellGlide();
    } else {
      this.soundManager.playStoneSlide();
    }

    // If castling, also animate the accompanying rook
    if (move.isCastling) {
      this.animateCastlingRook(move, duration);
    }

    this.activeMoves.push({
      piece: movingPiece,
      startPos,
      endPos,
      type: movingPiece.type,
      duration,
      elapsed: 0,
      soundPlayed: false,
      onComplete: () => {
        // Complete the move
        this.pieceView.updateSquare(movingPiece, move.to);
        movingPiece.mesh.position.copy(endPos);

        // Handle capture destruction
        if (move.captured) {
          this.handleCaptureDestruction(move, () => {
            this.handlePostMove(move, movingPiece, onFinish);
          });
        } else {
          this.soundManager.playStonePlace();
          this.handlePostMove(move, movingPiece, onFinish);
        }
      }
    });
  }

  private animateCastlingRook(move: MoveDetail, duration: number): void {
    let rookFrom: Square;
    let rookTo: Square;

    if (move.to === 'g1') {
      rookFrom = 'h1';
      rookTo = 'f1';
    } else if (move.to === 'c1') {
      rookFrom = 'a1';
      rookTo = 'd1';
    } else if (move.to === 'g8') {
      rookFrom = 'h8';
      rookTo = 'f8';
    } else {
      rookFrom = 'a8';
      rookTo = 'd8';
    }

    const rook = this.pieceView.getPieceAt(rookFrom);
    if (!rook) return;

    const startCoords = squareToCoords(rookFrom);
    const endCoords = squareToCoords(rookTo);
    const rStart = new THREE.Vector3(startCoords.x, rook.originalY, startCoords.z);
    const rEnd = new THREE.Vector3(endCoords.x, rook.originalY, endCoords.z);

    this.activeMoves.push({
      piece: rook,
      startPos: rStart,
      endPos: rEnd,
      type: 'r',
      duration,
      elapsed: 0,
      soundPlayed: true,
      onComplete: () => {
        this.pieceView.updateSquare(rook, rookTo);
        rook.mesh.position.copy(rEnd);
      }
    });
  }

  private handleCaptureDestruction(move: MoveDetail, onComplete: () => void): void {
    const targetSquare = move.capturedSquare || move.to;
    const capturedPiece = this.pieceView.getPieceAt(targetSquare);

    if (capturedPiece) {
      // Trigger shatter combat explosion
      this.soundManager.playCaptureShatter();
      this.createShatterExplosion(capturedPiece.mesh.position, capturedPiece.color);
      this.pieceView.removePiece(capturedPiece);
    }

    // Small delay for particles to disperse
    window.setTimeout(onComplete, this.animationSpeed === 'fast' ? 150 : 250);
  }

  private handlePostMove(move: MoveDetail, movingPiece: PieceObject, onFinish: () => void): void {
    // Handle pawn promotion
    if (move.promotion) {
      this.pieceView.removePiece(movingPiece);
      this.pieceView.createPiece(move.promotion, move.color, move.to);
      this.soundManager.playSpellGlide();
      this.createMagicAscensionBurst(squareToCoords(move.to));
    }

    if (move.isCheckmate) {
      this.soundManager.playVictory();
    } else if (move.isCheck) {
      this.soundManager.playCheck();
    }

    onFinish();
  }

  private executeInstant(move: MoveDetail, onFinish: () => void): void {
    const movingPiece = this.pieceView.getPieceAt(move.from);
    if (movingPiece) {
      const targetSquare = move.capturedSquare || move.to;
      const capturedPiece = this.pieceView.getPieceAt(targetSquare);
      if (capturedPiece) {
        this.pieceView.removePiece(capturedPiece);
      }

      this.pieceView.updateSquare(movingPiece, move.to);
      const dest = squareToCoords(move.to);
      movingPiece.mesh.position.set(dest.x, movingPiece.originalY, dest.z);

      if (move.isCastling) {
        const rookFrom = move.to === 'g1' ? 'h1' : move.to === 'c1' ? 'a1' : move.to === 'g8' ? 'h8' : 'a8';
        const rookTo = move.to === 'g1' ? 'f1' : move.to === 'c1' ? 'd1' : move.to === 'g8' ? 'f8' : 'd8';
        const rook = this.pieceView.getPieceAt(rookFrom as Square);
        if (rook) {
          this.pieceView.updateSquare(rook, rookTo as Square);
          const rDest = squareToCoords(rookTo as Square);
          rook.mesh.position.set(rDest.x, rook.originalY, rDest.z);
        }
      }

      if (move.promotion) {
        this.pieceView.removePiece(movingPiece);
        this.pieceView.createPiece(move.promotion, move.color, move.to);
      }

      this.soundManager.playStonePlace();
      if (move.isCheckmate) this.soundManager.playVictory();
      else if (move.isCheck) this.soundManager.playCheck();
    }

    onFinish();
  }

  // Combat Destruction: pieces break into stone fracture shards
  private createShatterExplosion(origin: THREE.Vector3, color: 'w' | 'b'): void {
    const shardCount = 20;
    const shardMat = new THREE.MeshStandardMaterial({
      color: color === 'w' ? 0xeae6dc : 0x22242b,
      roughness: 0.5,
      metalness: 0.3
    });

    for (let i = 0; i < shardCount; i++) {
      const size = 0.12 + Math.random() * 0.18;
      const geom = new THREE.BoxGeometry(size, size, size);
      const mesh = new THREE.Mesh(geom, shardMat);

      mesh.position.copy(origin).add(new THREE.Vector3(
        (Math.random() - 0.5) * 0.4,
        0.5 + Math.random() * 1.0,
        (Math.random() - 0.5) * 0.4
      ));

      this.particleGroup.add(mesh);

      const angle = Math.random() * Math.PI * 2;
      const speed = 2.5 + Math.random() * 4.5;
      const upVelocity = 3.0 + Math.random() * 3.5;

      this.activeShards.push({
        mesh,
        velocity: new THREE.Vector3(
          Math.cos(angle) * speed,
          upVelocity,
          Math.sin(angle) * speed
        ),
        rotationSpeed: new THREE.Vector3(
          (Math.random() - 0.5) * 15,
          (Math.random() - 0.5) * 15,
          (Math.random() - 0.5) * 15
        ),
        life: 0,
        maxLife: 0.8 + Math.random() * 0.4
      });
    }
  }

  // Pillar of magical light for Pawn Promotion
  private createMagicAscensionBurst(coords: { x: number; y: number; z: number }): void {
    const ringGeom = new THREE.CylinderGeometry(0.8, 0.8, 3.5, 16, 1, true);
    const ringMat = new THREE.MeshBasicMaterial({
      color: 0x00ffff,
      transparent: true,
      opacity: 0.8,
      side: THREE.DoubleSide
    });
    const cylinder = new THREE.Mesh(ringGeom, ringMat);
    cylinder.position.set(coords.x, coords.y + 1.7, coords.z);
    this.particleGroup.add(cylinder);

    // Fade out and dispose
    let prog = 0;
    const interval = setInterval(() => {
      prog += 0.05;
      ringMat.opacity = 0.8 * (1 - prog);
      cylinder.scale.y = 1 + prog * 0.5;
      if (prog >= 1) {
        clearInterval(interval);
        this.particleGroup.remove(cylinder);
        ringGeom.dispose();
        ringMat.dispose();
      }
    }, 20);
  }

  public update(delta: number): void {
    // 1. Process active piece move trajectories
    for (let i = this.activeMoves.length - 1; i >= 0; i--) {
      const anim = this.activeMoves[i];
      anim.elapsed += delta;
      const t = Math.min(1, anim.elapsed / anim.duration);

      // Trajectory calculation based on piece type
      if (anim.type === 'n') {
        // Knight: Ballistic hopping arc
        const ease = t;
        const x = THREE.MathUtils.lerp(anim.startPos.x, anim.endPos.x, ease);
        const z = THREE.MathUtils.lerp(anim.startPos.z, anim.endPos.z, ease);
        const arcHeight = 2.4;
        const y = anim.piece.originalY + Math.sin(t * Math.PI) * arcHeight;
        anim.piece.mesh.position.set(x, y, z);
      } else if (anim.type === 'p') {
        // Pawn: Stride with slight footstep wobble
        const ease = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
        const x = THREE.MathUtils.lerp(anim.startPos.x, anim.endPos.x, ease);
        const z = THREE.MathUtils.lerp(anim.startPos.z, anim.endPos.z, ease);
        const stepBob = Math.sin(t * Math.PI * 4) * 0.12;
        anim.piece.mesh.position.set(x, anim.piece.originalY + stepBob, z);
      } else {
        // Smooth slide / glide for King, Queen, Rook, Bishop
        const ease = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
        anim.piece.mesh.position.lerpVectors(anim.startPos, anim.endPos, ease);
      }

      if (t >= 1) {
        this.activeMoves.splice(i, 1);
        anim.onComplete();
      }
    }

    // 2. Process shattered stone physics
    const gravity = -14;
    for (let i = this.activeShards.length - 1; i >= 0; i--) {
      const shard = this.activeShards[i];
      shard.life += delta;

      // Physics update
      shard.velocity.y += gravity * delta;
      shard.mesh.position.addScaledVector(shard.velocity, delta);

      shard.mesh.rotation.x += shard.rotationSpeed.x * delta;
      shard.mesh.rotation.y += shard.rotationSpeed.y * delta;
      shard.mesh.rotation.z += shard.rotationSpeed.z * delta;

      // Floor bounce / clamp
      if (shard.mesh.position.y < 0.35) {
        shard.mesh.position.y = 0.35;
        shard.velocity.y = -shard.velocity.y * 0.3; // bounce damping
        shard.velocity.x *= 0.7;
        shard.velocity.z *= 0.7;
      }

      // Shrink and fade
      const remainingLife = 1 - shard.life / shard.maxLife;
      const scale = Math.max(0.01, remainingLife);
      shard.mesh.scale.set(scale, scale, scale);

      if (shard.life >= shard.maxLife) {
        this.particleGroup.remove(shard.mesh);
        shard.mesh.geometry.dispose();
        this.activeShards.splice(i, 1);
      }
    }
  }
}
